
import { Vue, Options, prop } from "vue-class-component";

export interface CategorizedItem {
  id: string | number;
  label: string;
  [key: string]: any; // Allow any additional fields
}

export interface FieldConfig {
  key: string;
  label?: string;
  displayKey: string;
  className?: string;
  showWhen?: (item: CategorizedItem) => boolean;
  formatter?: (value: any, item: CategorizedItem) => string;
}

export interface Category {
  key: string;
  label: string;
  icon: string;
  emptyText?: string;
  emptyImage?: string;
  fields?: FieldConfig[]; // Additional fields to display
}

class Props {
  modelValue = prop<CategorizedItem | null>({
    default: null,
    type: Object
  });
  placeholder = prop<string>({
    default: "Pilih item",
    type: String
  });
  disabled = prop<boolean>({
    default: false,
    type: Boolean
  });
  categories = prop<Category[]>({
    default: () => [],
    type: Array
  });
  searchPlaceholder = prop<string>({
    default: "Cari data",
    type: String
  });
  headerText = prop<string>({
    default: "Pilih item",
    type: String
  });
  onSearch = prop<Function>({
    default: () => Promise.resolve([]),
    type: Function
  });
  onLoadData = prop<Function>({
    default: () => Promise.resolve([]),
    type: Function
  });
  onLoadMore = prop<Function>({
    default: () => Promise.resolve([]),
    type: Function
  });
  hasMoreData = prop<Function>({
    default: () => false,
    type: Function
  });
  isLoadingData = prop<Function>({
    default: () => false,
    type: Function
  });
  onResetData = prop<Function>({
    default: () => Promise.resolve(),
    type: Function
  });
  minimumSearchChar = prop<number>({
    default: 3,
    type: Number
  });
  showTab = prop<boolean>({
    default: true,
    type: Boolean
  });
}

@Options({
  emits: ["update:modelValue", "change"],
  watch: {
    searchQuery: {
      handler: function() {
        if (this.isOpen) {
          this.handleSearch();
        }
      },
      immediate: false
    }
  },
  mounted() {
    // Initialize data when component is mounted
    this.initializeData();
  },
  beforeUnmount() {
    // Cleanup timeouts when component is destroyed
    if (this.searchTimeout) {
      clearTimeout(this.searchTimeout);
    }
    if (this.loadMoreTimeout) {
      clearTimeout(this.loadMoreTimeout);
    }
  }
})
export default class CategorizedSelector extends Vue.with(Props) {
  isOpen = false;
  searchQuery = "";
  activeTab = "";
  isLoading = false;
  isSearchMode = false;
  tabData: Record<string, CategorizedItem[]> = {};
  searchTimeout: NodeJS.Timeout | null = null;
  searchInput: HTMLInputElement | null = null;
  scrollPositions: Record<string, number> = {};
  loadMoreTimeout: NodeJS.Timeout | null = null;

  get selectedItem(): CategorizedItem | null {
    return this.modelValue;
  }

  get currentTabData(): CategorizedItem[] {
    const data = this.tabData[this.activeTab] || [];
    return data;
  }

  get currentCategory(): Category | undefined {
    return this.categories.find((category) => category.key === this.activeTab);
  }

  get currentEmptyText(): string {
    if (this.currentCategory?.emptyText) {
      return this.currentCategory.emptyText;
    }
    // Fallback based on category label
    if (this.currentCategory?.label) {
      return `${this.currentCategory.label} tidak ditemukan`;
    }
    return "Tidak ada data ditemukan";
  }

  get currentEmptyImage(): string {
    if (this.currentCategory?.emptyImage) {
      return this.currentCategory.emptyImage;
    }
    // Default fallback image
    return require("@/app/ui/assets/svg/ill_spot_da_out_of_range.svg");
  }

  get showMinimumCharMessage(): boolean {
    return (
      this.searchQuery.trim().length > 0 &&
      this.searchQuery.trim().length < this.minimumSearchChar
    );
  }

  get minimumCharMessage(): string {
    const remaining = this.minimumSearchChar - this.searchQuery.trim().length;
    return this.$t("dispatch.minimalCharacter", { remaining });
  }

  get currentCategoryFields(): FieldConfig[] {
    return this.currentCategory?.fields || [];
  }

  getItemFields(item: CategorizedItem): FieldConfig[] {
    return this.currentCategoryFields.filter((field) => {
      if (field.showWhen) {
        return field.showWhen(item);
      }
      return (
        item[field.key] !== undefined &&
        item[field.key] !== null &&
        item[field.key] !== ""
      );
    });
  }

  getFieldValue(field: FieldConfig, item: CategorizedItem): string {
    const value = item[field.key];
    if (field.formatter) {
      return field.formatter(value, item);
    }
    return value?.toString() || "";
  }

  focusSearchInput() {
    if (this.searchInput) {
      this.searchInput.focus();
    }
  }

  async toggleDropdown() {
    if (this.disabled) return;
    this.isOpen = !this.isOpen;
    if (this.isOpen) {
      this.resetToInitialData();
      this.$nextTick(() => {
        this.focusSearchInput();
      });
    }
  }

  async initializeData() {
    if (this.categories.length === 0) {
      return;
    }

    // Set active tab to first category if not set
    if (!this.activeTab) {
      this.activeTab = this.categories[0].key;
    }

    // Load data for all categories
    this.isLoading = true;
    try {
      const promises = this.categories.map((category) => {
        return this.loadTabData(category.key);
      });
      await Promise.allSettled(promises);
    } finally {
      this.isLoading = false;
    }
  }

  async loadTabData(tabKey: string) {
    try {
      const data = await this.onLoadData(tabKey);
      this.tabData[tabKey] = data;
    } catch (error) {
      this.tabData[tabKey] = [];
    }
  }

  setActiveTab(tabKey: string) {
    this.activeTab = tabKey;
    this.scrollToTop();

    // In search mode, never load new data - just switch tab
    if (this.isSearchMode) {
      return;
    }
    // Only load data if not already loaded
    if (!this.tabData[tabKey]) {
      this.loadTabData(tabKey);
    }
  }

  async handleSearch() {
    // Clear existing timeout
    if (this.searchTimeout) {
      clearTimeout(this.searchTimeout);
      this.searchTimeout = null;
    }

    if (!this.searchQuery.trim()) {
      await this.resetToInitialData();
      return;
    }

    // Minimum search characters required (configurable via prop)
    if (this.searchQuery.trim().length < this.minimumSearchChar) {
      // Don't clear tabData, just return to show minimum character message
      return;
    }

    // Debounce search - wait 300ms before executing
    this.searchTimeout = setTimeout(async () => {
      await this.executeSearch();
    }, 300);
  }

  private async resetToInitialData() {
    this.isSearchMode = false;
    this.onResetData();
    await this.initializeData();
  }

  private async executeSearch() {
    this.isSearchMode = true;
    this.isLoading = true;
    // Clear scroll positions when searching
    this.scrollPositions = {};
    try {
      const searchResults = await this.onSearch(this.searchQuery);
      this.tabData = searchResults;
      this.performSmartTabSwitching();
    } catch (error) {
      // console.error("Search failed:", error);
    } finally {
      this.isLoading = false;
    }
  }

  private performSmartTabSwitching() {
    if (
      !this.tabData[this.activeTab] ||
      this.tabData[this.activeTab].length === 0
    ) {
      const tabWithData = this.categories.find(
        (category) =>
          this.tabData[category.key] && this.tabData[category.key].length > 0
      );
      if (tabWithData) {
        this.activeTab = tabWithData.key;
      }
    }
  }

  closeDropdown() {
    this.isOpen = false;
    this.searchQuery = "";
  }

  beforeUnmount() {
    // Cleanup timeout when component is destroyed
    if (this.searchTimeout) {
      clearTimeout(this.searchTimeout);
      this.searchTimeout = null;
    }
  }

  selectItem(item: CategorizedItem) {
    this.$emit("update:modelValue", item);
    this.$emit("change", item);
    this.closeDropdown();
  }

  async loadMoreData() {
    if (
      !this.hasMoreData(this.activeTab) ||
      this.isLoadingData(this.activeTab)
    ) {
      return;
    }

    // Clear existing timeout
    if (this.loadMoreTimeout) {
      clearTimeout(this.loadMoreTimeout);
    }

    // Simple debounce like DexAssessmentController - 1 second delay
    this.loadMoreTimeout = setTimeout(async () => {
      try {
        const moreData = await this.onLoadMore(this.activeTab);

        if (moreData && moreData.length > 0) {
          // Append new data to existing data
          this.tabData[this.activeTab] = [
            ...(this.tabData[this.activeTab] || []),
            ...moreData
          ];
        }
      } catch (error) {
        // console.error("Failed to load more data:", error);
      }
    }, 1000); // 1 second delay like DexAssessmentController
  }

  handleScroll(event: Event) {
    const target = event.target as HTMLElement;
    const { scrollTop, scrollHeight, clientHeight } = target;

    // Save current scroll position
    this.scrollPositions[this.activeTab] = scrollTop;

    // Load more when scrolled to bottom (with 50px threshold)
    if (scrollTop + clientHeight >= scrollHeight - 50) {
      this.loadMoreData();
    }
  }

  scrollToTop() {
    const scrollContainer = this.$el.querySelector(
      ".max-h-64.overflow-y-auto"
    ) as HTMLElement;
    if (scrollContainer) {
      scrollContainer.scrollTop = 0;
    }
  }

  get chevronUpIcon() {
    return require("@/app/ui/assets/svg/chevron-up.svg");
  }

  get chevronDownIcon() {
    return require("@/app/ui/assets/svg/chevron-down.svg");
  }
}
